import React, { createContext, useContext, useEffect, useMemo } from 'react'
import { useBandwidth } from '@fs/zion-bandwidth'
import {
  getBrightspotWebConfig,
  setLiveImageSizes,
  type BrightspotConfig,
  type ImageSizes,
} from '../../config/brightspot'
import normalizeBandwidth from './normalizeBandwidth'
import useViewport from './useViewport'

const BrightspotConfigContext = createContext<BrightspotConfig | undefined>(undefined)

type Props = { children: React.ReactNode }

// The only place useBandwidth() is called. Everything else — consumers of the default
// brightspotConfig export that only read its static fields, and the non-component GraphQL
// builder modules that read getLiveImageSizes() — stays untouched by this.
export const BrightspotConfigProvider = ({ children }: Props): JSX.Element => {
  const treatment = useBandwidth()
  const bandwidth = normalizeBandwidth(treatment)
  const { width, dpr } = useViewport()

  const config = useMemo(() => getBrightspotWebConfig(bandwidth, width, dpr), [bandwidth, width, dpr])

  useEffect(() => {
    setLiveImageSizes(config.imageSizes)
  }, [config])

  return <BrightspotConfigContext.Provider value={config}>{children}</BrightspotConfigContext.Provider>
}

const useBrightspotConfigContext = (): BrightspotConfig => {
  const context = useContext(BrightspotConfigContext)
  if (!context) throw new Error('useBrightspotConfig must be used within a BrightspotConfigProvider')
  return context
}

export const useBrightspotConfig = (): BrightspotConfig => useBrightspotConfigContext()

export const useBrightspotImageSizes = (): ImageSizes => useBrightspotConfigContext().imageSizes
